/**
 * The app shell.
 *
 * T3's layout, measured from its live DOM rather than guessed:
 *
 * - A sidebar and a chat column, with the chat column capped at
 *   `--chat-content-max-width` (46rem) and centered. On a 1366px iPad that cap
 *   is the single biggest readability win — a prompt box spanning the full width
 *   is unreadable.
 * - Below 900px it collapses to one pane at a time, driven by whether a chat is
 *   open. That is the phone and the 50/50 split on an iPad.
 * - `env(safe-area-inset-*)` throughout, so the composer clears the home
 *   indicator and the toolbar clears the status bar in standalone mode.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppState, useStore } from "./store-context";
import { Sidebar } from "./sidebar";
import { AppSidebarLayout } from "./app-sidebar-layout";
import { ChatPane } from "./chat-pane";
import { NewChatScreen } from "./new-chat";

/** What the right-hand pane is showing. T3 keeps this as a route; here it is one
 * piece of state, because there is exactly one chat open at a time. */
export type PaneView = "chat" | "new";

export function App() {
  const state = useAppState();
  const store = useStore();
  const [view, setView] = useState<PaneView>("chat");
  /** Narrow screens show one pane; this is which one. */
  const [narrowShowsChat, setNarrowShowsChat] = useState(false);

  const openChat = useCallback(
    (chatId: string) => {
      setView("chat");
      setNarrowShowsChat(true);
      void store.openChat(chatId);
    },
    [store]
  );

  const startNewChat = useCallback(() => {
    setView("new");
    setNarrowShowsChat(true);
    store.openNewChat();
  }, [store]);

  // The new-chat screen closing (Create or Cancel) returns the pane to the chat.
  const newChatOpen = state.newChat !== undefined;
  useEffect(() => {
    if (!newChatOpen && view === "new") setView("chat");
  }, [newChatOpen, view]);

  const onBack = useCallback(() => setNarrowShowsChat(false), []);

  const body = useMemo(() => {
    if (state.auth === "anonymous") return null;
    return (
      <AppSidebarLayout
        sidebar={
          <Sidebar
            hidden={narrowShowsChat}
            onOpenChat={openChat}
            onNewChat={startNewChat}
          />
        }
        sidebarHidden={narrowShowsChat}
      >
        {view === "new" ? (
          <NewChatScreen onCreated={() => setView("chat")} onCancel={() => setView("chat")} />
        ) : (
          <ChatPane onBack={onBack} />
        )}
      </AppSidebarLayout>
    );
  }, [state.auth, narrowShowsChat, openChat, startNewChat, view, onBack]);

  return <div className="h-full">{body}</div>;
}
